"use client";

import { ChevronDown, Phone } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import {
  pickerCountries,
  countryOf,
  reconcileTyped,
  type CountryCode,
  type PhoneValue,
} from "./phone-countries";

/**
 * The phone input used by signup and by both WhatsApp pairing steps.
 *
 * The country chip is a PICKER whenever there is more than one country to
 * pick. It once was a static `🇬🇧 +44` span with no recognition behind it, so a
 * non-UK number was not refused — it was quietly rewritten into a UK one
 * nobody could ring. With only the UK offered it is a label again, but the
 * recognition stays: paste a +49 number and the picker comes back showing 🇩🇪.
 *
 * The pure half (assembly, splitting, validation) lives in ./phone-countries
 * so it can be tested and called without pulling a client component in.
 */
interface PhoneFieldProps {
  value: PhoneValue;
  onChange: (v: PhoneValue) => void;
  error?: string | null;
}

export function PhoneField({ value, onChange, error }: PhoneFieldProps) {
  const country = countryOf(value.country);
  const options = pickerCountries(value.country);

  return (
    <div className="mucka-auth-field">
      <div className="mucka-auth-phone-row">
        {options.length > 1 ? (
          /* The chevron is not decoration. `.mucka-auth-cc` carries
             `appearance: none`, so without it a select renders as a flat chip
             indistinguishable from a static label — a control nobody can tell
             is a control is the bug, one layer up. */
          <div className="mucka-auth-cc-wrap">
            <select
              className="mucka-auth-cc"
              aria-label="Country code"
              value={value.country}
              onChange={(e) =>
                // Only the country moves. Retyping the number because they picked
                // the wrong flag first is exactly the re-entry anti-pattern #35 is
                // about, on a smaller scale.
                onChange({ ...value, country: e.target.value as CountryCode })
              }
            >
              {options.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.dial}
                </option>
              ))}
            </select>
            <span className="mucka-auth-cc-caret" aria-hidden="true">
              <Icon icon={ChevronDown} size={15} strokeWidth={2.5} />
            </span>
          </div>
        ) : (
          /* Only one country on offer, so there is nothing to pick. Safe as a
             plain label because a pasted number in a known non-UK code still
             moves `value.country` (reconcileTyped), and the moment it does
             this becomes the picker again showing that country. */
          <span className="mucka-auth-cc mucka-auth-cc--static" aria-label="Country code">
            {country.flag} {country.dial}
          </span>
        )}
        <div
          className={`mucka-auth-input-wrap mucka-auth-phone-input${error ? " mucka-auth-input-wrap--error" : ""}`}
        >
          <span className="mucka-auth-input-icon">
            <Icon icon={Phone} size={18} strokeWidth={2} />
          </span>
          <input
            className="mucka-auth-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={value.local}
            // reconcileTyped, not a bare setState: pasting a full +49 number
            // has to MOVE the chip, or the field shows one country and sends
            // another.
            onChange={(e) => onChange(reconcileTyped({ ...value, local: e.target.value }))}
            placeholder={country.placeholder}
            aria-label="Phone number"
          />
        </div>
      </div>
      {error && <div className="mucka-auth-field-error">{error}</div>}
    </div>
  );
}

export {
  buildFullPhone,
  validatePhone,
  splitE164,
  formatDisplayPhone,
  EMPTY_PHONE,
  type PhoneValue,
} from "./phone-countries";
